import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Hidden,
  Stack,
  Tab,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ApiUser } from 'types';
import { VideoPreviewWithControls } from 'components';
import { MyRoom } from '../MyRoom';
import { WelcomeLayout } from '../WelcomeLayout';
import { EditWaitingRoom } from 'pages/EditRoomPage/components/EditWaitingRoom';
import { useVideo } from 'hooks';

import styles from './MyWaitingRoom.module.scss';
import { AvailableMark } from '../AvailableMark';

type MyWaitingRoomProps = {
  className?: string;
  onDirtyChange?(dirty: boolean): void;
};

export function MyWaitingRoom({ className, onDirtyChange }: MyWaitingRoomProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl =
    i18n.resolvedLanguage?.toLowerCase().startsWith('ar') ||
    i18n.dir(i18n.resolvedLanguage) === 'rtl' ||
    (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');
  const user: ApiUser | null = useAppSelector(selectUser);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { getAudioAndVideoTracks } = useVideo();
  const [activeTab, setActiveTab] = useState<'myRoom' | 'editWaitingRoom'>('myRoom');
  const [isEditWaitingRoomDirty, setIsEditWaitingRoomDirty] = useState(false);

  const handleEditWaitingRoomDirtyChange = (dirty: boolean): void => {
    setIsEditWaitingRoomDirty(dirty);
    onDirtyChange?.(dirty);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: 'myRoom' | 'editWaitingRoom') => {
    if (activeTab === 'editWaitingRoom' && newValue === 'myRoom' && isEditWaitingRoomDirty) {
      if (!window.confirm(t('unsavedChangesPrompt'))) {
        return;
      }

      setIsEditWaitingRoomDirty(false);
    }

    setActiveTab(newValue);
  };

  const cardHeaderTitle = useMemo(() => {
    let title: React.ReactNode;
    switch (true) {
      case isDesktop: {
        title = (
          <TabList onChange={handleChange} aria-label={t('waitingRoomTabsAria')}>
            <Tab label={t('myRoomTab')} value='myRoom' disableRipple disableTouchRipple />
            <Tab label={t('editWaitingRoomTab')} value='editWaitingRoom' disableRipple disableTouchRipple />
          </TabList>
        );
        break;
      }
      default: {
        title = (
          <Stack
            direction='row'
            alignItems='center'
            className={styles.mobileHeaderRow}
            sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
          >
            <Typography component='span' variant='body2' className={styles.mobileHeaderTitle}>
              {t('myRoomTab')}
            </Typography>
            <AvailableMark className={styles.availableMark} />
          </Stack>
        );
        break;
      }
    }
    return title;
  }, [isDesktop, isRtl, t]);

  useEffect(() => {
    if (activeTab === 'editWaitingRoom') {
      getAudioAndVideoTracks();
    }
  }, [activeTab, getAudioAndVideoTracks]);

  return (
    <>
      <TabContext value={activeTab}>
        <Card
          className={classnames(styles.main, className)}
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined,
            boxShadow: theme.palette.mode === 'dark' ? 'none' : 'rgba(0, 0, 0, 0.05) 0px 2px 4px',
          })}
        >
          {!user?.clinicUser.roomName ? (
            <WelcomeLayout />
          ) : (
            <Box>
              <CardHeader title={cardHeaderTitle} className={styles.cardHeader} />

              <CardContent className={styles.content}>
                <Box
                  className={styles.contentWrapper}
                  maxWidth={isDesktop ? 510 : '100%'}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                  })}
                >
                  {activeTab === 'myRoom' ? (
                    <MyRoom />
                  ) : (
                    <EditWaitingRoom onDirtyChange={handleEditWaitingRoomDirtyChange} />
                  )}
                </Box>
                <Hidden only={['sm', 'xs']}>
                  <Box className={styles.preview}>
                    <VideoPreviewWithControls />
                  </Box>
                </Hidden>
              </CardContent>
            </Box>
          )}
        </Card>
      </TabContext>
    </>
  );
}
